import { FC, useState, useReducer, useEffect } from 'react'
import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Button,
} from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'

import {
  TimeTypeEnum,
  ItemEntry,
  EditItemRequest,
  EditItemCycleRatePlanRequest,
  EntryType,
  Challan,
  WEIGHT_CHARGE_ITEM_TYPE,
  isLostEntry,
} from '../../../services'

// Components
import { AppDialog, ChallanSection } from '../../../components'

// Redux
import { useAppDispatch, useAppSelector } from '../../../hooks'
import {
  updateIssuedItemAction,
  updateIssuedItemResetAction,
} from '../../../store/items'
import {
  fetchChallansAction,
  fetchChallansResetAction,
  refreshFetchSlipsAction,
} from '../../../store/slips'
import { showAlertAction } from '../../../store/alerts'
import { getFormattedDate, replace12AmWithCurrentTime } from '../../../utils'
import { fetchCustomerAccountAction } from '../../../store/customerAccounts'
import CustomNumberField from '../Users/profile/CustomNumberField'
import { fetchEntryById } from './services'
import {
  type RentCycleMode,
  type ExtraDayChargeMode,
  formatPerDayFromCycle,
  hydrateExtraDayFromPlan,
  cycleRatePlanForRequestStub,
} from './editCycleRatePlanUtils'

type IssueItemFields =
  | 'quantity'
  | 'rate'
  | 'weight'
  | 'lostItemCost'
  | 'minRentalDays'
  | 'height'
  | 'width'
  | 'length'
  | 'hsnCode'
type IssueItemFieldsState = Record<
  IssueItemFields,
  Partial<{
    value: string
    error: boolean
    helperText: string
  }>
>

type IssueItemErrors = Record<IssueItemFields, Record<string, string>>

const issueItemErrors: IssueItemErrors = {
  quantity: {
    empty: 'Please enter quantity',
  },
  rate: {
    empty: 'Please enter rate',
  },
  weight: {
    empty: 'Please enter weight',
  },
  height: {
    empty: 'Please enter height',
  },
  width: {
    empty: 'Please enter width',
  },
  length: {
    empty: 'Please enter length',
  },
  lostItemCost: {
    empty: 'Enter total cost',
  },
  minRentalDays: {
    empty: 'Enter total cost',
  },
  hsnCode: {
    empty: 'Please enter HSN/SAC Code',
  },
}

const initialItemIssueState: IssueItemFieldsState = {
  quantity: {
    error: false,
    helperText: 'Please enter quantity',
  },
  rate: {
    error: false,
    helperText: 'Please enter rate',
  },
  weight: {
    error: false,
    helperText: 'Please enter weight',
  },
  height: {
    error: false,
    helperText: 'Please enter height',
  },
  width: {
    error: false,
    helperText: 'Please enter width',
  },
  length: {
    error: false,
    helperText: 'Please enter length',
  },
  lostItemCost: {
    error: false,
    helperText: 'Enter total cost',
  },
  minRentalDays: {
    error: false,
    helperText: 'Enter total cost',
  },
  hsnCode: {
    error: false,
    helperText: 'Please enter HSN/SAC Code',
  },
}
const itemReducer = (state: Partial<IssueItemFieldsState>, action) => {
  const {
    type,
    payload: { name, value },
    helperText,
  } = action
  switch (type) {
    case 'SET':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: false,
          value: value,
          helperText: helperText
            ? helperText
            : initialItemIssueState[name]?.helperText,
        },
      }
    case 'ERROR':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: true,
          value: value,
          helperText: issueItemErrors[name]['empty'],
        },
      }
    case 'RESET':
      return {
        ...initialItemIssueState,
        [name]: {
          ...state[name],
          error: false,
          value: value,
          helperText: initialItemIssueState[name]?.helperText,
        },
      }
    default:
      return state
  }
}

export interface EditItemProps {
  children?: React.ReactNode
  onClose?: () => void
  onSubmitSuccess?: () => void
  entryId: number
  siteId: number
  challanNo: string
  entryType: string
  challanType: string
}

export const EditItem: FC<EditItemProps> = ({
  onClose,
  onSubmitSuccess,
  entryId,
  siteId,
  challanNo,
  entryType,
  challanType
}) => {
  const dispatchAction = useAppDispatch()
  const {
    success: updateIssuedItemSuccess,
    error: updateIssuedItemError,
    message,
  } = useAppSelector((state) => state.updateIssuedItems)

  const {
    list: { selectedTab },
  } = useAppSelector((state) => state.manageCustomerAccounts)


  const [item, setItem] = useState<ItemEntry | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [date, setDate] = useState<Date | null>(null)
  const [timeType, setTimeType] = useState<TimeTypeEnum>()
  const [isClose, setClosePopup] = useState<boolean>(false)
  const [itemData, dispatch] = useReducer(itemReducer, initialItemIssueState)
  const [request, setRequest] = useState<EditItemRequest | null>(null)
  const { data: userData } = useAppSelector((state) => state.userData)
  const [submissionType, setSubmissionType] = useState<string>()
  const [selectedChallan, setSelectedChallan] = useState<Challan | null>(null)
  const [userDefinedChallanNumber, setDefinedChallanNumber] = useState('')
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false)
  const [rentCycleMode, setRentCycleMode] = useState<RentCycleMode>('DAILY')
  const [customCycleDays, setCustomCycleDays] = useState<number>(30)
  const [cyclePlanId, setCyclePlanId] = useState<number | undefined>(undefined)
  const [slabRows, setSlabRows] = useState<{ uptoDays: string; charge: string }[]>([
    { uptoDays: '', charge: '' },
  ])
  const [slabError, setSlabError] = useState(false)
  const [extraDayChargeMode, setExtraDayChargeMode] =
    useState<ExtraDayChargeMode>('PER_DAY_RATE')
  const [perDaySideField, setPerDaySideField] = useState('')

  const fetchItemEntry = async () => {
      setLoading(true)
      try {
       
        const response = await fetchEntryById(entryId)
        if (response.success) {
          const fetchedItem = response.data
          setItem(fetchedItem)
          setTimeType(fetchedItem?.timeType)
        } else {
          dispatchAction(showAlertAction({
            message: response.message || 'Failed to load entry',
            severity: 'error'
          }))
        }
      } catch (error) {
        dispatchAction(showAlertAction({
          message: 'Failed to load entry',
          severity: 'error'
        }))
      } finally {
        
        setLoading(false)
      }
    }
  
  useEffect(() => {
    if (entryId) {
      fetchItemEntry()
    } else {
      setLoading(false)
    }
  }, [entryId])

   useEffect(() => {
    if(item) {
       setTimeType(item.timeType)
    }
  }, [item])

  useEffect(() => {
    if(item) {
    const submissionType =
      item?.entryType === EntryType.IN && isLostEntry(item) ? 'LOST' : ''
    setSubmissionType(submissionType)
  }
  }, [item])

  useEffect(() => {
    if (!item) return

    const plan = item.cycleRatePlan
    const showCycleRentUi =
      item.entryType === EntryType.OUT &&
      item.isConsumable !== true &&
      !item.isArea
    const isOutCycle =
      item.entryType === EntryType.OUT && plan != null && showCycleRentUi

    if (item.entryType === EntryType.OUT) {
      if (plan && showCycleRentUi) {
        setRentCycleMode(plan.cycleDays === 30 ? 'MONTHLY' : 'CUSTOM')
        setCustomCycleDays(plan.cycleDays)
        setCyclePlanId(plan.id)
        const hydrated = hydrateExtraDayFromPlan(plan)
        setExtraDayChargeMode(hydrated.mode)
        setSlabRows(hydrated.slabRows)
        setPerDaySideField(hydrated.perDaySideField)
      } else {
        setRentCycleMode('DAILY')
        setCustomCycleDays(30)
        setCyclePlanId(undefined)
        setExtraDayChargeMode('PER_DAY_RATE')
        setSlabRows([{ uptoDays: '', charge: '' }])
        setPerDaySideField('')
      }
    } else {
      setRentCycleMode('DAILY')
      setCustomCycleDays(30)
      setCyclePlanId(undefined)
      setExtraDayChargeMode('PER_DAY_RATE')
      setSlabRows([{ uptoDays: '', charge: '' }])
      setPerDaySideField('')
    }
    setSlabError(false)

    dispatch({
      type: 'SET',
      payload: { name: 'quantity', value: item.quantity },
    })
    const rateForForm = isOutCycle ? plan!.cycleCharge : item.rate
    dispatch({ type: 'SET', payload: { name: 'rate', value: rateForForm } })
    if (item?.itemType !== WEIGHT_CHARGE_ITEM_TYPE) {
      dispatch({
        type: 'SET',
        payload: { name: 'weight', value: item.weight },
      })
    }

    dispatch({
      type: 'SET',
      payload: { name: 'lostItemCost', value: item.lostItemCost },
    })

    dispatch({ type: 'SET', payload: { name: 'height', value: item.height } })
    dispatch({ type: 'SET', payload: { name: 'width', value: item.width } })
    dispatch({ type: 'SET', payload: { name: 'length', value: item.length } })

    if (userData.config.isMinDaysEnabled || item.minRentalDays > 1) {
      dispatch({
        type: 'SET',
        payload: { name: 'minRentalDays', value: item.minRentalDays },
      })
    }

    dispatch({ type: 'SET', payload: { name: 'hsnCode', value: item.hsnCode } })

    setRequest({
      ...(item.entryDate && {
        entryDate: getFormattedDate(new Date(item.entryDate)),
      }),
      ...(item.height != null && item.height !== '' && { height: item.height }),
      ...(item.width != null && item.width !== '' && { width: item.width }),
      ...(item.length != null && item.length !== '' && { length: item.length }),
      ...(item.quantity != null && { quantity: item.quantity }),
      ...(item.entryType === EntryType.OUT && {
        rate: isOutCycle ? 0 : item.rate,
        ...(isOutCycle &&
          plan && {
            cycleRatePlan: cycleRatePlanForRequestStub(plan),
          }),
      }),
      ...(item?.itemType !== WEIGHT_CHARGE_ITEM_TYPE &&
        item?.weight != null && { weight: item.weight }),
      ...(item.timeType && { timeType: item.timeType }),
      ...(item.lostItemCost &&
        submissionType == 'LOST' && { lostItemCost: item.lostItemCost }),
      ...(item.hsnCode && { hsnCode: item.hsnCode }),
      ...((userData.config.isMinDaysEnabled || item.minRentalDays > 1) && {
        minRentalDays: item.minRentalDays,
      }),
      id: item.id,
      challanNo: selectedChallan?.slipNo
        ? selectedChallan?.slipNo
        : item.challanNo,
      challanId: selectedChallan?.id as unknown as string,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, submissionType, selectedChallan])

  useEffect(() => {
    if (updateIssuedItemSuccess) {
      dispatchAction(refreshFetchSlipsAction())
      dispatchAction(fetchCustomerAccountAction(selectedTab))

      dispatchAction(
        showAlertAction({
          message: 'Item Updated Successfully',
          severity: 'success',
        })
      )
      onSubmitSuccess() // To close the popup
    } else if (updateIssuedItemError && message) {
      dispatchAction(showAlertAction({ message: message, severity: 'error' }))
      onSubmitSuccess() // To close the popup
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateIssuedItemSuccess, updateIssuedItemError, message])

  const onChangeHandler = (e) => {
    const { name, valueAsNumber, value } = e.target
    const parsed =
      value === '' || value == null
        ? NaN
        : typeof valueAsNumber === 'number' && !Number.isNaN(valueAsNumber)
        ? valueAsNumber
        : Number(value)
    if (!Number.isNaN(parsed)) {
      dispatch({ type: 'SET', payload: { name, value: parsed } })
      setRequest((prev) => (prev ? { ...prev, [name]: parsed } : prev))
    } else {
      dispatch({ type: 'ERROR', payload: { name, value: value } })
      setRequest((prev) => (prev ? { ...prev, [name]: value } : prev))
    }
  }

  const onTextChangeHandler = (e) => {
    const { name, value } = e.target
    dispatch({ type: 'SET', payload: { name, value: value } })
    setRequest((prev) => (prev ? { ...prev, [name]: value } : prev))
  }

  const onRentCycleModeChange = (event: SelectChangeEvent<RentCycleMode>) => {
    const mode = event.target.value as RentCycleMode
    const wasDaily = rentCycleMode === 'DAILY'
    setRentCycleMode(mode)
    setSlabError(false)
    if (mode === 'DAILY') {
      setExtraDayChargeMode('PER_DAY_RATE')
      setPerDaySideField('')
    } else if (wasDaily) {
      setExtraDayChargeMode('PER_DAY_RATE')
      setSlabRows([{ uptoDays: '', charge: '' }])
      const rateVal = Number(itemData['rate']?.value)
      const days = mode === 'MONTHLY' ? 30 : customCycleDays
      if (rateVal > 0 && days > 0 && !Number.isNaN(rateVal)) {
        setPerDaySideField(formatPerDayFromCycle(rateVal, days))
      } else {
        setPerDaySideField('')
      }
    }
  }

  const onExtraDayChargeModeChange = (
    event: SelectChangeEvent<ExtraDayChargeMode>
  ) => {
    const v = event.target.value as ExtraDayChargeMode
    const prev = extraDayChargeMode
    setExtraDayChargeMode(v)
    setSlabError(false)
    const rateVal = Number(itemData['rate']?.value)
    const days = rentCycleMode === 'MONTHLY' ? 30 : customCycleDays

    if (v === 'PER_DAY_RATE') {
      if (rateVal > 0 && days > 0 && !Number.isNaN(rateVal)) {
        setPerDaySideField(formatPerDayFromCycle(rateVal, days))
      } else {
        setPerDaySideField('')
      }
    } else if (v === 'PER_DAY_FIXED' && prev !== 'PER_DAY_FIXED') {
      setPerDaySideField('')
    } else if (v === 'BY_DAY_RANGE') {
      setSlabRows((rows) =>
        rows.length ? rows : [{ uptoDays: '', charge: '' }]
      )
    }
  }

  const onPerDaySideFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPerDaySideField(e.target.value)
  }

  useEffect(() => {
    if (extraDayChargeMode !== 'PER_DAY_RATE') return
    if (rentCycleMode === 'DAILY') return
    const rateVal = Number(itemData.rate?.value)
    const days = rentCycleMode === 'MONTHLY' ? 30 : customCycleDays
    if (
      !rateVal ||
      rateVal <= 0 ||
      !days ||
      days <= 0 ||
      Number.isNaN(rateVal)
    ) {
      return
    }
    setPerDaySideField(formatPerDayFromCycle(rateVal, days))
  }, [extraDayChargeMode, rentCycleMode, customCycleDays, itemData.rate?.value])

  const onCustomCycleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (raw === '' || raw == null) {
      setCustomCycleDays(0)
      return
    }
    const n = parseInt(String(raw), 10)
    if (!Number.isNaN(n)) setCustomCycleDays(n)
  }

  const updateSlab = (index: number, field: 'uptoDays' | 'charge', v: string) => {
    setSlabRows((rows) =>
      rows.map((r, i) => (i === index ? { ...r, [field]: v } : r))
    )
    setSlabError(false)
  }

  const addSlabRow = () => {
    setSlabRows((rows) =>
      rows.length >= 3 ? rows : [...rows, { uptoDays: '', charge: '' }]
    )
  }

  const removeSlabRow = (index: number) => {
    setSlabRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, i) => i !== index)))
    setSlabError(false)
  }

  const buildSlabsForApi = () =>
    slabRows
      .map((r) => ({
        uptoDays: Number(r.uptoDays),
        charge: Number(r.charge),
      }))
      .filter(
        (s) =>
          !Number.isNaN(s.uptoDays) &&
          s.uptoDays > 0 &&
          !Number.isNaN(s.charge) &&
          s.charge >= 0
      )

  const dateChangeHandler = (value) => {
    const newDate = replace12AmWithCurrentTime(value)
    setRequest((prev) =>
      prev ? { ...prev, entryDate: getFormattedDate(newDate) } : prev
    )
    setDate(newDate)
  }

  const timeTypeChangeHandler = (event: SelectChangeEvent) => {
    const v = event.target.value as TimeTypeEnum
    setRequest((prev) => (prev ? { ...prev, timeType: v } : prev))
    setTimeType(v)
  }

  const submitHandle = () => {
    if (!request || !item) return

    const challanNoVal = userDefinedChallanNumber
      ? userDefinedChallanNumber
      : selectedChallan?.slipNo
    const challanIdVal = selectedChallan?.id as unknown as string

    const base: EditItemRequest = {
      ...request,
      challanNo: challanNoVal,
      challanId: challanIdVal,
    }

    if (selectedChallan?.id !== 0) {
      delete base.entryDate
    }

    if (selectedChallan === null || selectedChallan === undefined) {
      return
    }

    if (item?.itemType === WEIGHT_CHARGE_ITEM_TYPE) {
      if (base?.quantity == null || (base?.quantity !== 0 && !base?.quantity)) {
        dispatch({
          type: 'ERROR',
          payload: { name: 'quantity', value: base?.quantity },
        })
        return
      }
    }

    const rateVal = Number(itemData['rate']?.value)
    const showCycleRentUi =
      item.isConsumable !== true && !item.isArea
    if (item.entryType === EntryType.OUT && submissionType !== 'LOST') {
      if (showCycleRentUi && rentCycleMode !== 'DAILY') {
        if (!rateVal || rateVal <= 0) {
          dispatch({
            type: 'ERROR',
            payload: { name: 'rate', value: itemData['rate']?.value },
          })
          return
        }
        if (rentCycleMode === 'CUSTOM') {
          if (
            !customCycleDays ||
            customCycleDays < 1 ||
            customCycleDays > 365 ||
            !Number.isInteger(customCycleDays)
          ) {
            dispatchAction(
              showAlertAction({
                message: 'Cycle days must be an integer between 1 and 365',
                severity: 'error',
              })
            )
            return
          }
        }
        const effectiveCycleDays =
          rentCycleMode === 'MONTHLY' ? 30 : customCycleDays

        const cyclePlanBase: Pick<
          EditItemCycleRatePlanRequest,
          'id' | 'cycleDays' | 'cycleCharge'
        > = {
          id: cyclePlanId,
          cycleDays: rentCycleMode === 'MONTHLY' ? 30 : customCycleDays,
          cycleCharge: rateVal,
        }

        if (extraDayChargeMode === 'BY_DAY_RANGE') {
          for (const row of slabRows) {
            const hasU = row.uptoDays !== ''
            const hasC = row.charge !== ''
            if (hasU !== hasC) {
              setSlabError(true)
              dispatchAction(
                showAlertAction({
                  message:
                    'Complete both Extra days and Charge for each slab row',
                  severity: 'error',
                })
              )
              return
            }
            if (hasU) {
              const d = Number(row.uptoDays)
              const c = Number(row.charge)
              if (Number.isNaN(d) || d <= 0 || Number.isNaN(c) || c < 0) {
                setSlabError(true)
                dispatchAction(
                  showAlertAction({
                    message: 'Invalid slab row values',
                    severity: 'error',
                  })
                )
                return
              }
            }
          }

          const uptoForDup: number[] = []
          for (const row of slabRows) {
            if (row.uptoDays !== '') {
              const d = Number(row.uptoDays)
              if (!Number.isNaN(d) && d > 0) uptoForDup.push(d)
            }
          }
          if (uptoForDup.length !== new Set(uptoForDup).size) {
            setSlabError(true)
            dispatchAction(
              showAlertAction({
                message: 'Each Extra days value must be unique',
                severity: 'error',
              })
            )
            return
          }

          for (const row of slabRows) {
            if (row.uptoDays === '') continue
            const d = Number(row.uptoDays)
            if (Number.isNaN(d) || d <= 0) continue
            if (d > effectiveCycleDays) {
              setSlabError(true)
              dispatchAction(
                showAlertAction({
                  message: `Extra days must be less than or equal to cycle days (${effectiveCycleDays})`,
                  severity: 'error',
                })
              )
              return
            }
          }
          base.rate = 0
          base.cycleRatePlan = {
            ...cyclePlanBase,
            extraDayChargeSlabs: buildSlabsForApi(),
          }
        } else if (extraDayChargeMode === 'PER_DAY_FIXED') {
          const p = Number(perDaySideField)
          if (Number.isNaN(p) || p <= 0 || !Number.isFinite(p)) {
            dispatchAction(
              showAlertAction({
                message: 'Enter a valid per day fixed amount',
                severity: 'error',
              })
            )
            return
          }
          base.rate = 0
          base.cycleRatePlan = { ...cyclePlanBase, perDayFixed: p }
        } else {
          const p = Number(perDaySideField)
          if (Number.isNaN(p) || p <= 0 || !Number.isFinite(p)) {
            dispatchAction(
              showAlertAction({
                message: 'Enter a valid per day rate',
                severity: 'error',
              })
            )
            return
          }
          base.rate = 0
          base.cycleRatePlan = { ...cyclePlanBase, perDayRate: p }
        }
      } else {
        base.rate = Number.isNaN(rateVal) ? item.rate : rateVal
        delete base.cycleRatePlan
      }
    }

    if (base.id == null || base.challanId == null) return

    dispatchAction(updateIssuedItemAction(base))
  }

  useEffect(() => {
    return () => {
      dispatchAction(fetchChallansResetAction())
      dispatchAction(updateIssuedItemResetAction()) // To manage the Alert
    }
  }, [])

  return (
    <AppDialog
      id="edit-item"
      title={item?.itemName ?? 'Edit Item'}
      submitButtonText="Submit"
      onClose={onClose}
      onSubmit={submitHandle}
      close={isClose}
      maxWidth="sm"
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            width: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12} sx={{ pb: 2, mx: 2 }} mt={1.5}>
          <Box
            component="form"
            noValidate
            autoComplete="off"
          >
            <Grid container spacing={3}>
            {item && item?.isConsumable !== true && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="select-time-helper-label" shrink={!!timeType}>
                      Select Time
                    </InputLabel>
                    {item.entryType === 'IN' && (
                      <Select
                        size="small"
                        labelId="select-time-helper-label"
                        id="select-time"
                        value={timeType || ''}
                        name="timeType"
                        label="Select Time"
                        onChange={timeTypeChangeHandler}
                        notched={!!timeType}
                      >
                        <MenuItem value={TimeTypeEnum.MORNING}>
                          Morning (Day will not be counted)
                        </MenuItem>
                        <MenuItem value={TimeTypeEnum.EVENING}>
                          Evening
                        </MenuItem>
                      </Select>
                    )}

                    {item.entryType === 'OUT' && (
                      <Select
                        size="small"
                        labelId="select-time-helper-label"
                        id="select-time"
                        value={timeType || ''}
                        name="timeType"
                        label="Select Time"
                        onChange={timeTypeChangeHandler}
                        notched={!!timeType}
                      >
                        <MenuItem value={TimeTypeEnum.MORNING}>
                          Morning
                        </MenuItem>
                        <MenuItem value={TimeTypeEnum.EVENING}>
                          Evening (Day will not be counted)
                        </MenuItem>
                      </Select>
                    )}
                  </FormControl>
                </Grid>
            )}

            {item && item.isArea && (
              <>
                <Grid item xs={12}>
                  <CustomNumberField
                    maxLength={8}
                    name="height"
                    value={itemData.height.value}
                    label="Height"
                    onChange={onChangeHandler}
                    fullWidth
                    helperText={
                      itemData['height']['error']
                          ? itemData.height.helperText
                          : ''
                    }
                    error={itemData['height']['error']}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomNumberField
                    maxLength={8}
                    name="width"
                    value={itemData.width.value}
                    label="Width"
                    onChange={onChangeHandler}
                    fullWidth
                    helperText={
                      itemData['width']['error']
                          ? itemData.width.helperText
                          : ''
                    }
                    error={itemData['width']['error']}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomNumberField
                    maxLength={8}
                    name="length"
                    value={itemData.length.value}
                    label="Length"
                    onChange={onChangeHandler}
                    fullWidth
                    helperText={
                      itemData['length']['error']
                          ? itemData.length.helperText
                          : ''
                    }
                    error={itemData['length']['error']}
                  />
                </Grid>
              </>
            )}

            {item && (item?.isConsumable !== true && !item?.isArea || item?.itemType === WEIGHT_CHARGE_ITEM_TYPE) && (
                <Grid item xs={12}>
                  <CustomNumberField
                    maxLength={8}
                    name="quantity"
                    value={itemData.quantity.value}
                    label="Quantity"
                    onChange={onChangeHandler}
                    fullWidth
                    helperText={
                      itemData['quantity']['error']
                        ? itemData.quantity.helperText
                        : ''
                    }
                    error={itemData['quantity']['error']}
                  />
                </Grid>
            )}
            {item && item?.entryType === EntryType.OUT && (() => {
              const showCycleRentUi =
                item.isConsumable !== true && !item.isArea
              return (
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid
                    item
                    xs={12}
                    sm={
                      showCycleRentUi
                        ? rentCycleMode === 'DAILY'
                          ? 6
                          : 4
                        : 12
                    }
                  >
                    <CustomNumberField
                      maxLength={8}
                      name="rate"
                      value={itemData.rate.value}
                      label={
                        item?.itemType === WEIGHT_CHARGE_ITEM_TYPE
                          ? 'Rate'
                          : item?.isConsumable
                            ? 'Amount'
                            : 'Rate'
                      }
                      onChange={onChangeHandler}
                      fullWidth
                      error={itemData['rate']['error']}
                      helperText={
                        itemData['rate']['error'] ? itemData.rate.helperText : ''
                      }
                    />
                  </Grid>
                  {showCycleRentUi && (
                  <Grid
                    item
                    xs={12}
                    sm={rentCycleMode === 'DAILY' ? 6 : 4}
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel id="edit-rent-cycle-label">Rent cycle</InputLabel>
                      <Select
                        labelId="edit-rent-cycle-label"
                        label="Rent cycle"
                        value={rentCycleMode}
                        onChange={onRentCycleModeChange}
                      >
                        <MenuItem value="DAILY">Daily</MenuItem>
                        <MenuItem value="MONTHLY">Monthly</MenuItem>
                        <MenuItem value="CUSTOM">Custom</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  )}
                  {showCycleRentUi && rentCycleMode !== 'DAILY' && (
                    <Grid item xs={12} sm={4}>
                      <CustomNumberField
                        maxLength={3}
                        name="editCycleDays"
                        label="Cycle days"
                        value={
                          rentCycleMode === 'MONTHLY'
                            ? 30
                            : customCycleDays === 0
                              ? ''
                              : customCycleDays
                        }
                        onChange={onCustomCycleDaysChange}
                        fullWidth
                        disabled={rentCycleMode === 'MONTHLY'}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  )}
                  {showCycleRentUi &&
                    (rentCycleMode === 'MONTHLY' || rentCycleMode === 'CUSTOM') && (
                    <Grid container item xs={12} spacing={2}>
                      <Grid item xs={12} sm={8}>
                        <FormControl fullWidth size="small">
                          <InputLabel
                            id="edit-extra-day-charge-mode-label"
                            shrink
                          >
                            How to charge for extra days?
                          </InputLabel>
                          <Select
                            labelId="edit-extra-day-charge-mode-label"
                            label="How to charge for extra days?"
                            value={extraDayChargeMode}
                            onChange={onExtraDayChargeModeChange}
                          >
                            <MenuItem value="PER_DAY_RATE">Per Day Rate</MenuItem>
                            <MenuItem value="PER_DAY_FIXED">
                              Per Day Fixed
                            </MenuItem>
                            <MenuItem value="BY_DAY_RANGE">
                              By Day Range (different charges based on days)
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {(extraDayChargeMode === 'PER_DAY_RATE' ||
                        extraDayChargeMode === 'PER_DAY_FIXED') && (
                        <Grid item xs={12} sm={4}>
                          <CustomNumberField
                            maxLength={12}
                            name="perDaySideField"
                            label={
                              extraDayChargeMode === 'PER_DAY_RATE'
                                ? 'Per day Rate'
                                : 'Per day fixed'
                            }
                            value={perDaySideField}
                            onChange={onPerDaySideFieldChange}
                            fullWidth
                            InputLabelProps={{ shrink: !!perDaySideField }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}
                  {showCycleRentUi &&
                    (rentCycleMode === 'MONTHLY' || rentCycleMode === 'CUSTOM') &&
                    extraDayChargeMode === 'BY_DAY_RANGE' &&
                    slabRows.map((row, idx) => {
                      const showAdd =
                        idx === slabRows.length - 1 && slabRows.length < 3
                      return (
                        <Grid
                          container
                          item
                          xs={12}
                          key={idx}
                          spacing={2}
                          sx={{ mt: -2.5 }}
                        >
                          <Grid item xs={12} sm={4}>
                            <CustomNumberField
                              maxLength={6}
                              name={`slab-upto-${idx}`}
                              label="Extra days"
                              value={row.uptoDays}
                              onChange={(e) =>
                                updateSlab(idx, 'uptoDays', e.target.value)
                              }
                              fullWidth
                              error={slabError}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <CustomNumberField
                              maxLength={8}
                              name={`slab-charge-${idx}`}
                              label="Charge"
                              value={row.charge}
                              onChange={(e) =>
                                updateSlab(idx, 'charge', e.target.value)
                              }
                              fullWidth
                              error={slabError}
                            />
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={4}
                            sx={{
                              pt: { xs: 0, sm: '22px' },
                              display: 'flex',
                              alignItems: 'stretch',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1,
                                width: '100%',
                                alignItems: 'stretch',
                                minHeight: 40,
                              }}
                            >
                              <Button
                                type="button"
                                variant="contained"
                                disableElevation
                                aria-label="Remove row"
                                onClick={() => removeSlabRow(idx)}
                                disabled={slabRows.length <= 1}
                                sx={{
                                  flex: showAdd ? 1 : '1 1 auto',
                                  maxWidth: 40,
                                  minWidth: 0,
                                  minHeight: 40,
                                  height: 40,
                                  maxHeight: 40,
                                  alignSelf: 'flex-start',
                                  p: 0,
                                  bgcolor: '#c62828',
                                  color: '#fff',
                                  '&:hover': { bgcolor: '#b71c1c' },
                                  '&:disabled': {
                                    bgcolor: 'action.disabledBackground',
                                    color: 'action.disabled',
                                  },
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </Button>
                              {showAdd ? (
                                <Button
                                  type="button"
                                  variant="contained"
                                  disableElevation
                                  aria-label="Add row"
                                  onClick={addSlabRow}
                                  sx={{
                                    flex: 1,
                                    maxWidth: 40,
                                    minWidth: 40,
                                    minHeight: 40,
                                    height: 40,
                                    maxHeight: 40,
                                    alignSelf: 'flex-start',
                                    p: 0,
                                    bgcolor: '#2e7d32',
                                    color: '#fff',
                                    '&:hover': { bgcolor: '#1b5e20' },
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </Button>
                              ) : null}
                            </Box>
                          </Grid>
                        </Grid>
                      )
                    })}
                </Grid>
              </Grid>
              )
            })()}

            {submissionType === 'LOST' && (
                <Grid item xs={12}>
                  <CustomNumberField
                    maxLength={8}
                    name="lostItemCost"
                    value={itemData.lostItemCost.value}
                    label="Total Cost"
                    onChange={onChangeHandler}
                    fullWidth
                    error={itemData['lostItemCost']['error']}
                    helperText={
                      itemData['lostItemCost']['error']
                        ? itemData.lostItemCost.helperText
                        : ''
                    }
                    InputLabelProps={{
                      shrink: !!itemData.lostItemCost.value,
                    }}
                  />
                </Grid>
            )}

            {item && item?.entryType === EntryType.OUT &&
              item?.isConsumable !== true &&
              (userData.config.isMinDaysEnabled || item.minRentalDays > 1) && (
                <Grid item xs={12}>
                  <CustomNumberField
                      maxLength={3}
                      name="minRentalDays"
                      value={itemData.minRentalDays.value}
                      label="Min Rental Days"
                      onChange={onChangeHandler}
                      fullWidth
                      error={itemData['minRentalDays']['error']}
                      helperText={
                        itemData['minRentalDays']['error']
                          ? itemData.minRentalDays.helperText
                          : ''
                      }
                    />
                </Grid>
              )}

            {item && (
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    name="hsnCode"
                    value={itemData.hsnCode.value || ''}
                    label="HSN/SAC Code"
                    onChange={onTextChangeHandler}
                    fullWidth
                    error={itemData['hsnCode']['error']}
                    helperText={
                      itemData['hsnCode']['error']
                        ? itemData.hsnCode.helperText
                        : ''
                    }
                  />
                </Grid>
            )}
            {siteId && challanNo && (
                <Grid item xs={12}>
            <ChallanSection
              challanNo={challanNo}
              initialChallanId={item?.challanId}
              userDefinedChallanNumber={userDefinedChallanNumber}
              setDefinedChallanNumber={setDefinedChallanNumber}
              selectedChallan={selectedChallan}
              setSelectedChallan={setSelectedChallan}
              bill_id={siteId}
              setReadyToSubmit={() => {}}
              datePickerOpen={datePickerOpen}
              setDatePickerOpen={setDatePickerOpen}
              date={date}
              setDate={dateChangeHandler}
              singleRow={false}
              challan_type={challanType}
              mdValue={0}
            />
                </Grid>
            )}
            </Grid>
          </Box>
        </Grid>
      </Grid>
      )}
    </AppDialog>
  )
}
